<script lang="ts" setup>
import {
  onMounted,
  onUnmounted,
  watchEffect,
  ref,
  provide,
  useSlots,
  h,
  withDirectives,
  vShow,
  useId,
  type ComponentPublicInstance,
} from 'vue';
import { unrefElement } from '@vueuse/core';
import { injectStrict, isVNodeNotEmpty, asArray } from '../../utils';
import { ABSTRACT_DISCLOSURE_CONTEXT, ABSTRACT_DISCLOSURE_PANEL_CONTEXT } from './shared';
interface Props {
  id?: string;
  unmount?: boolean;
}
const { id = useId(), unmount = true } = defineProps<Props>();
const { panelRef, panelId, expanded } = injectStrict(
  ABSTRACT_DISCLOSURE_CONTEXT,
  undefined,
  '<ObAbstractDisclosurePanel /> must be a child of <ObAbstractDisclosure /> component.',
);
onMounted(() => {
  panelId.value = id;
});
onUnmounted(() => {
  panelId.value = null;
});
provide(ABSTRACT_DISCLOSURE_PANEL_CONTEXT, panelId);
const elementRef = ref<HTMLElement | null>(null);
watchEffect(() => {
  panelRef.value = elementRef.value;
});
const slots = useSlots();
defineRender(() => {
  if (unmount && !expanded.value) {
    return null;
  }
  const children = slots.default?.({
    rootProps: {
      id,
      ref: (el: Element | ComponentPublicInstance | null) => {
        elementRef.value = unrefElement(el as any);
      },
    },
  });
  if (isVNodeNotEmpty(children)) {
    if (unmount) {
      return children;
    }
    const normalized = asArray(children);
    if (normalized.length === 1) {
      return withDirectives(normalized[0], [[vShow, expanded.value]]);
    }
    return withDirectives(
      h(
        'div',
        {
          id,
          ref: (el: Element | ComponentPublicInstance | null) => {
            elementRef.value = unrefElement(el as any);
          },
        },
        normalized,
      ),
      [[vShow, expanded.value]],
    );
  }
  return null;
});
</script>
